import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Slide,
    Typography,
    useMediaQuery,
} from '@mui/material';
import React, { useRef } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import Error from '../atoms/Error';
import Locale from '../Locale';
import PropTypes from 'prop-types';
import SubmitButton from '../SubmitButton';
import { makeStyles } from 'tss-react/mui';
import styles from './ResponsiveDialog.styles';
import { useTheme } from '@mui/material/styles';
import { v4 as uuid } from 'uuid';

const useStyles = makeStyles({ name: 'ResponsiveDialog' })(styles);

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const ResponsiveDialog = React.forwardRef((props, ref) => {
    const {
        title,
        subtitle,
        disclaimer,
        children,
        classes: providedClasses,
        actions,
        error,
        formId,
        isConfirmDisabled,
        isLoading,
        loadingButtonIndicator = true,
        loadingText,
        backAction,
        onBack,
        backText = 'common.back',
        onConfirm,
        cancelButtonProps,
        confirmTrackEvent,
        confirmText = 'common.submit',
        confirmButtonProps,
        onClose,
        onCancel,
        cancelTrackEvent,
        cancelText = 'common.close',
        open,
        showCloseButton,
        translationOptions,
        delegateScrollingToChildren,
        confirmVariant,
        container,
        sectioned,
        ...remain
    } = props;
    const { classes: styleClasses, cx } = useStyles({ sectioned });
    const classes = { ...styleClasses, ...providedClasses };
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const id = useRef(uuid());

    return (
        <Dialog
            {...remain}
            ref={ref}
            classes={providedClasses}
            container={container?.current}
            open={!!open}
            onClose={onClose}
            fullScreen={fullScreen}
            aria-labelledby={id.current}
            TransitionComponent={Transition}
        >
            <DialogTitle id={id.current} className={classes.title}>
                {typeof title === 'string' ? (
                    <Typography component="p" variant="h4">
                        <Locale path={title} options={translationOptions} />
                    </Typography>
                ) : (
                    title
                )}
                {subtitle ? (
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                        <Locale path={subtitle} options={translationOptions} />
                    </Typography>
                ) : null}
                {onClose && (fullScreen || showCloseButton) ? (
                    <IconButton
                        aria-label="close"
                        className={classes.closeButton}
                        onClick={onClose}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                ) : null}
            </DialogTitle>
            {!loadingButtonIndicator && isLoading && (
                <span className={classes.loadingOverlay}>
                    <CircularProgress />
                </span>
            )}
            <DialogContent
                className={classes.content}
                classes={{
                    root: isLoading
                        ? classes.noScroll
                        : delegateScrollingToChildren
                          ? classes.delegateScroll
                          : undefined,
                }}
            >
                {typeof children === 'string' ? (
                    <Locale path={children} options={translationOptions} />
                ) : (
                    children
                )}
            </DialogContent>
            {onBack || onCancel || onConfirm || (actions && actions.length) ? (
                <DialogActions className={classes.actions}>
                    {backAction ? (
                        backAction
                    ) : onBack ? (
                        <Button
                            key="backButton"
                            onClick={onBack}
                            disabled={isLoading}
                            color="tertiary"
                            variant="text"
                            startIcon={<ArrowBackIcon />}
                        >
                            <Locale path={backText} options={translationOptions} />
                        </Button>
                    ) : null}
                    {onCancel || onConfirm || (actions && actions.length) ? (
                        <div className={classes.defaultActions}>
                            {onCancel && (
                                <Button
                                    key="cancelButton"
                                    variant="outlined"
                                    onClick={onCancel}
                                    className={classes.cancelButton}
                                    disabled={isLoading}
                                    data-track={cancelTrackEvent}
                                    data-testid="cancel-button"
                                    {...cancelButtonProps}
                                >
                                    <Locale path={cancelText} options={translationOptions} />
                                </Button>
                            )}
                            {actions && actions.length ? actions : null}
                            {onConfirm && (
                                <SubmitButton
                                    key="confirmButton"
                                    onClick={onConfirm}
                                    autoFocus
                                    loading={loadingButtonIndicator ? isLoading : false}
                                    loadingText={loadingText}
                                    disabled={isLoading || isConfirmDisabled}
                                    variant="contained"
                                    color={confirmVariant || 'primary'}
                                    className={classes.submitButton}
                                    data-track={confirmTrackEvent}
                                    formId={formId}
                                    data-testid="confirm-button"
                                    {...confirmButtonProps}
                                >
                                    {typeof confirmText === 'string' ? (
                                        <Locale path={confirmText} options={translationOptions} />
                                    ) : (
                                        confirmText
                                    )}
                                </SubmitButton>
                            )}
                        </div>
                    ) : null}
                </DialogActions>
            ) : null}
            {disclaimer && (
                <Typography
                    variant="caption"
                    display="block"
                    gutterBottom
                    className={cx(classes.body, classes.disclaimer)}
                >
                    <Locale path={disclaimer} options={translationOptions} />
                </Typography>
            )}
            {error && (
                <Error gutterBottom className={classes.body}>
                    <Locale path={error} options={translationOptions} />
                </Error>
            )}
        </Dialog>
    );
});

ResponsiveDialog.displayName = 'ResponsiveDialog';

ResponsiveDialog.propTypes = {
    cancelButtonProps: PropTypes.object,
    classes: PropTypes.object,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    subtitle: PropTypes.string,
    disclaimer: PropTypes.string,
    error: PropTypes.string,
    children: PropTypes.node,
    actions: PropTypes.arrayOf(PropTypes.node),
    backAction: PropTypes.node,
    backText: PropTypes.string,
    cancelText: PropTypes.string,
    cancelTrackEvent: PropTypes.string,
    confirmText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    confirmButtonProps: PropTypes.object,
    confirmTrackEvent: PropTypes.string,
    container: PropTypes.object,
    sectioned: PropTypes.bool,
    formId: PropTypes.string,
    isConfirmDisabled: PropTypes.bool,
    isLoading: PropTypes.bool,
    loadingButtonIndicator: PropTypes.bool,
    loadingText: PropTypes.string,
    open: PropTypes.bool,
    onBack: PropTypes.func,
    onCancel: PropTypes.func,
    onClose: PropTypes.func,
    onConfirm: PropTypes.func,
    showCloseButton: PropTypes.bool,
    translationOptions: PropTypes.object,
    delegateScrollingToChildren: PropTypes.bool,
    confirmVariant: PropTypes.string,
};

ResponsiveDialog.defaultProps = {
    delegateScrollingToChildren: false,
};

export default ResponsiveDialog;
